import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { ChevronLeft, Bot, Sparkles, Loader2, AlertCircle, CheckCircle2, Zap, ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TRIGGER_TYPES, ACTION_TYPES } from './automationConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLE_PROMPTS = [
  "When a job reaches 'Completed', wait 3 days, then text the customer asking for a review and notify the office.",
  "When someone texts 'quote' or 'estimate', ask them what type of project they need, wait for reply, then assign to a sales rep.",
  "Send a follow-up email 2 days after an estimate is sent if not signed.",
  "When a task becomes overdue, send a notification to the assigned user and create a reminder task.",
  "Every Monday at 9am, send a weekly summary email to all ops managers."
];

export const AIAutomationBuilder = ({ onResult, onBack }) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a description');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.post(`${API}/automations/ai-generate`, { prompt });
      
      if (response.data.success) {
        setResult(response.data.automation);
      } else {
        setError(response.data.error || 'Failed to generate automation');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setError(err.response?.data?.detail || 'Failed to generate automation');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseAutomation = () => {
    if (result) {
      onResult(result);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="h-6 w-6 text-purple-500" />
            AI Automation Builder
          </h1>
          <p className="text-sm text-muted-foreground">Describe what you want in plain English</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Left - Input */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-yellow-500" />
                Describe Your Automation
              </CardTitle>
              <CardDescription>
                Tell me what you want to automate in plain English. Be specific about triggers, timing, and actions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="When a job reaches 'Completed', wait 3 days, then text the customer asking for a review..."
                rows={6}
                className="resize-none"
              />
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !prompt.trim()}
                className="w-full"
              >
                {isGenerating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> Generate Automation</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Example Prompts */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Example Prompts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {EXAMPLE_PROMPTS.map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => setPrompt(example)}
                  className="w-full text-left p-2 text-sm rounded-md hover:bg-accent transition-colors"
                >
                  "{example.slice(0, 80)}..."
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Right - Result */}
        <div className="space-y-4">
          {error && (
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/30">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3 text-red-700 dark:text-red-400">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Generation Failed</p>
                    <p className="text-sm mt-1">{error}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {result && (
            <Card className="border-green-200 bg-green-50 dark:bg-green-950/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2 text-green-700 dark:text-green-400">
                    <CheckCircle2 className="h-5 w-5" />
                    Automation Generated
                  </CardTitle>
                  <Badge variant="outline">Requires Approval</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Name</p>
                  <p className="font-medium">{result.name}</p>
                </div>

                {result.description && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Description</p>
                    <p className="text-sm">{result.description}</p>
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Triggers</p>
                  <div className="flex flex-wrap gap-2">
                    {result.triggers?.map((trigger, idx) => {
                      const config = TRIGGER_TYPES[trigger.type];
                      const Icon = config?.icon || Zap;
                      return (
                        <Badge key={idx} className={config?.color}>
                          <Icon className="h-3 w-3 mr-1" />
                          {config?.label || trigger.type}
                        </Badge>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Actions Flow</p>
                  <div className="flex items-center flex-wrap gap-2">
                    {result.actions?.map((action, idx) => {
                      const config = ACTION_TYPES[action.type];
                      const Icon = config?.icon || Zap;
                      return (
                        <React.Fragment key={idx}>
                          {idx > 0 && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
                          <Badge variant="secondary">
                            <Icon className="h-3 w-3 mr-1" />
                            {config?.label || action.type}
                          </Badge>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {result.actions?.some(a => a.message_template) && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Message Preview</p>
                    <div className="bg-white dark:bg-slate-900 p-3 rounded-md text-sm border">
                      {result.actions.find(a => a.message_template)?.message_template}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button onClick={handleUseAutomation} className="flex-1">
                    Use This Automation
                  </Button>
                  <Button variant="outline" onClick={() => setResult(null)}>
                    Regenerate
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {!result && !error && !isGenerating && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <Bot className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">AI-Generated Automation</p>
                <p className="text-sm text-center max-w-xs mt-2">
                  Your generated automation will appear here. Review it before activating.
                </p>
              </CardContent>
            </Card>
          )}

          {isGenerating && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-12 w-12 mb-4 animate-spin text-purple-500" />
                <p className="text-lg font-medium">Generating Automation...</p>
                <p className="text-sm text-muted-foreground mt-2">
                  AI is analyzing your request and building the automation
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
